import React, { useContext } from 'react';
import { isEmpty } from 'lodash';
import BaseNode from '../../BaseNode';
import { useBaseStyles } from '../style';
import {
  calculateDynamicParamsHeight,
  calculateNodeHeightByParams,
  isDefinedNodeParam,
  putPropsToEventHandler,
  shouldShowSocketIn,
} from '../../../helpers/node';
import { COLUMN_WIDTH } from '../../../helpers/hierarchy';
import Header from './Header';
import Phases from './Phases';
import SessionIndicator from './SessionIndicator';
import EditControls from './EditControls';
import { SchemaDisplayContext } from '../../../context';
import ConnectionSocketIn from '../../ConnectionSocketIn';
import { VIEW_SCHEMA_MODE } from '../../../../constants';

const renderNodeParams = (params, styles) => params.map(
  (param) => (isDefinedNodeParam(param) ? (
    <div
      key={param.label}
      className={styles.staticParamsItem}
    >
      {param.label}
      :&nbsp;
      <strong
        className={styles.staticParamsValue}
        title={param.value}
      >
        {param.value}
      </strong>
    </div>
  ) : null),
);

function BaseBlockNode(props) {
  const {
    plaza: { isActive, locked },
    node: {
      connectingSourceNode, isConnected, shapedDynamicSection, click, mouseDown, mouseUp,
    },
    schema: { mode },
  } = useContext(SchemaDisplayContext);
  const { hidePhases = false } = props;

  const DnDEvents = locked ? {} : {
    onClick: putPropsToEventHandler(click, props),
    onMouseDown: putPropsToEventHandler(mouseDown, props),
    onMouseUp: putPropsToEventHandler(mouseUp, props),
  };

  const isMonitoring = mode === VIEW_SCHEMA_MODE;

  const styles = useBaseStyles();
  const {
    width = COLUMN_WIDTH,
    infoMarkers = null,
    phases,
    chargerUuid,
    staticParams = [],
    dynamicParams = [],
  } = props;

  let height = calculateNodeHeightByParams(infoMarkers, staticParams, dynamicParams);

  height += !!chargerUuid && isMonitoring ? 5 : 0;
  if (!infoMarkers && isEmpty(staticParams) && isEmpty(dynamicParams)) {
    height -= 10;
  }

  const paramsSectionConfig = {
    x: 5,
    y: infoMarkers ? 65 : 35,
    width: width - 10,
    height: height - 30 - (infoMarkers ? 30 : 0),
  };

  const plugInSectionConfig = {
    x: (width / 2) - 11,
    y: paramsSectionConfig.y + paramsSectionConfig.height - 24,
    width: 20,
    height: 20,
  };

  const dynamicSectionHeight = calculateDynamicParamsHeight(dynamicParams);

  return (
    <BaseNode {...props}>
      <rect
        className={styles.body}
        width={width}
        height={height}
      />
      <EditControls {...props} width={width} />
      <Header
        {...props}
        width={width}
        dndEvents={DnDEvents}
      />
      {
        infoMarkers && (
          <svg
            x={1}
            y={32}
            width={width - 2}
            height={30}
          >
            <rect
              className={styles.infoMarkersBg}
              width="100%"
              height="100%"
            />
            {infoMarkers}
          </svg>
        )
      }
      {!hidePhases && (
        <Phases
          phases={phases}
          nodeWidth={width}
        />
      )}
      <foreignObject {...paramsSectionConfig} {...DnDEvents}>
        {
          staticParams.length > 0 && (
            <div className={styles.staticParams}>
              {renderNodeParams(staticParams, styles)}
            </div>
          )
        }
        {
          dynamicParams.length > 0 && !shapedDynamicSection && (
            <div className={styles.dynamicParams}>
              {renderNodeParams(dynamicParams, styles)}
            </div>
          )
        }
        {
          shapedDynamicSection && dynamicSectionHeight > 0 && (
            <div
              className={styles.dynamicParams}
              style={{ height: `${dynamicSectionHeight}px` }}
            />
          )
        }
      </foreignObject>
      {
        isActive && shouldShowSocketIn(connectingSourceNode, props, isConnected(props.id)) && (
          <foreignObject {...plugInSectionConfig}>
            <ConnectionSocketIn {...props} className="plug-in block-node" />
          </foreignObject>
        )
      }
      {chargerUuid && isMonitoring ? (
        <SessionIndicator
          chargerProps={props}
          nodeWidth={width}
          nodeHeight={height}
        />
      ) : null}
    </BaseNode>
  );
}

export default BaseBlockNode;
